import { storageKeys } from '@/lib/storage-keys'

/**
 * Imported-stop tracker — client-only, localStorage-backed.
 *
 * WHY: a long tour routing can exceed MAX_IMPORTABLE_STOPS (12), so a user
 * imports it in several <=12-stop batches. This tracker remembers which stop
 * ids have already been imported for a given import source so that, on
 * re-opening the Import dialog, StopSelectStep can show which stops are
 * already done and the user can pick up where they left off.
 *
 * Scope: per-browser, per-device — there is no backend for this. Clearing
 * browser storage clears the memory; that is the acceptable privacy tradeoff
 * for a tool with no accounts. No PII leaves the device (stop ids only).
 *
 * Shape: a JSON array of stop-id strings stored under one key per tour.
 *
 * Resilience: every storage access is wrapped in try/catch and guarded by an
 * SSR check, so quota-exceeded, private-mode Safari, disabled storage, or
 * server-side rendering can never throw — reads degrade to an empty Set and
 * writes degrade to a silent no-op.
 */

/** Build the per-source storage key. */
function storageKey(sourceKey: string): string {
	return storageKeys.imported(sourceKey)
}

/**
 * True only in a browser with a usable localStorage. SSR (no `window`) and
 * environments where `localStorage` is missing both return false so callers
 * fall back to safe defaults instead of throwing.
 */
function hasStorage(): boolean {
	return typeof window !== 'undefined' && !!window.localStorage
}

/**
 * Parse a stored raw value into a Set of stop ids. Tolerates a missing,
 * non-JSON, or wrong-shaped value by returning an empty Set — a corrupt entry
 * must never break the import flow.
 */
function parseIds(raw: string | null): Set<string> {
	if (!raw) return new Set<string>()
	try {
		const parsed: unknown = JSON.parse(raw)
		if (!Array.isArray(parsed)) return new Set<string>()
		return new Set(parsed.filter((id): id is string => typeof id === 'string'))
	} catch {
		return new Set<string>()
	}
}

/**
 * Read the set of stop ids already imported for `tourId`.
 *
 * WHY: lets StopSelectStep mark already-done stops when the dialog re-opens
 * mid-way through a multi-batch import of a long tour.
 *
 * SSR-safe (returns an empty Set with no `window`) and never throws — a
 * missing or corrupt stored value yields an empty Set.
 */
export function getImportedStopIds(tourId: string): Set<string> {
	if (!hasStorage()) return new Set<string>()
	try {
		return parseIds(window.localStorage.getItem(storageKey(tourId)))
	} catch {
		return new Set<string>()
	}
}

/**
 * Mark `ids` as imported for `tourId`, unioning them with anything already
 * recorded (dedup via Set) and persisting the result.
 *
 * WHY: after each <=12-stop batch is imported, the picked stop ids are added
 * here so the next batch's dialog knows what is already done.
 *
 * SSR-safe and never throws — on no `window` or a storage failure (quota,
 * private mode, disabled storage) it is a silent no-op.
 */
export function markStopsImported(tourId: string, ids: string[]): void {
	if (!hasStorage()) return
	try {
		const merged = getImportedStopIds(tourId)
		for (const id of ids) merged.add(id)
		window.localStorage.setItem(storageKey(tourId), JSON.stringify([...merged]))
	} catch {
		// Quota exceeded / private mode / disabled storage — acceptable to drop.
	}
}

/**
 * Forget all imported stops for `tourId` (removes the storage key).
 *
 * WHY: lets a user start the multi-batch import of a tour over from scratch.
 *
 * SSR-safe and never throws — on no `window` or a storage failure it is a
 * silent no-op.
 */
export function clearImportedStops(tourId: string): void {
	if (!hasStorage()) return
	try {
		window.localStorage.removeItem(storageKey(tourId))
	} catch {
		// Storage unavailable — nothing to do.
	}
}
